import { PageKey } from '../../shared/models/page-key.enum';
import { QuickDatePreset } from '../../shared/services/date-presets/date-range.service';
import { CompareMode } from './date-range-defaults.service';

export type FilterId = string;

export type FilterSelectionType = 'multi' | 'select' | 'boolean' | 'text' | 'dateRange';

/** Origin of the persisted compare window. Drives cross-page reconciliation. */
export type CompareIntent = 'preset' | 'user';

export type FilterSelection =
  | {
      type: 'multi';
      /**
       * Storage shape sent to the API. May be the policy-flipped complement
       * for large multi-select sets (e.g. NOT IN list when user actually
       * picked the minority). Use this for SQL; use ``uiValues`` for display.
       */
      values: string[];
      /**
       * Literal values the user picked in the dropdown, in their mental model
       * (always IN-style — what's checked). Optional so older selections still
       * work; chip rendering falls back to ``values`` when absent.
       */
      uiValues?: string[];
    }
  | { type: 'select'; value: string | null }
  | { type: 'boolean'; value: boolean | null }
  | { type: 'text'; value: string }
  | {
      type: 'dateRange';
      value: {
        start: string;
        end: string;
        compareStart?: string;
        compareEnd?: string;
        compareIntent?: CompareIntent;
        // Set only when compareIntent='preset'.
        compareMode?: CompareMode;
        // Set only when compareIntent='preset'. Required for recompute:
        // calendar-aligned presets (lastMonth/lastQuarter/lastYear) and
        // rolling ones (last30/last90) reshape compare differently.
        sourcePreset?: QuickDatePreset;
      };
    };

type Scalar = string | number | boolean;

export interface FilterSchemaItem {
  displayName: string;
  column: string; // API column name
  type: FilterSelectionType;
  default?: unknown;
  /**
   * Operator used when applying default/current selections for this filter to API column filters.
   * 'in' (default) => selections go to in_filters; 'not_in' => selections go to not_in_filters.
   */
  defaultOperator?: 'in' | 'not_in';
  options?: string[];
  // optional metadata for UI-backed dynamic options
  source?: 'api' | 'static';
  fetchDistinct?: boolean;
  orderDirection?: 'asc' | 'desc';
  /** Collapsed multi-select summary copy; from tenant `filters.filters.<id>.pluralLabel`. */
  pluralLabel?: string;
  /** UI-only formatting for option labels; from tenant `filters.filters.<id>.displayFormat`. */
  displayFormat?: 'dateOnly';
}

export interface FilterSchema {
  filters: Record<FilterId, FilterSchemaItem>;
  pageApplicability?: Partial<Record<PageKey, FilterId[]>>;
  applyMode?: 'debounce' | 'onBlur' | 'manual';
  urlSync?: boolean;
}

/** IN filter - column value in list */
interface InColumnFilter {
  col: string;
  values: Scalar[]; // min 1 item
}

/** NOT IN filter - column value not in list */
interface NotInColumnFilter {
  col: string;
  values: Scalar[]; // min 1 item
}

/** BETWEEN filter - column value between two values */
interface BetweenColumnFilter {
  col: string;
  values: [Scalar, Scalar]; // exactly 2 items [start, end]
}

/** NOT BETWEEN filter */
interface NotBetweenColumnFilter {
  col: string;
  values: [Scalar, Scalar]; // exactly 2 items [start, end]
}

/** IS NULL filter - no value needed */
interface IsNullColumnFilter {
  col: string;
}

/** IS NOT NULL filter - no value needed */
interface IsNotNullColumnFilter {
  col: string;
}

/** Greater than (>) filter - single value */
interface GreaterThanColumnFilter {
  col: string;
  value: Scalar; // single value, not array
}

/** Greater than or equal to (>=) filter - single value */
interface GreaterThanOrEqualToColumnFilter {
  col: string;
  value: Scalar; // single value, not array
}

/** Less than (<) filter - single value */
interface LessThanColumnFilter {
  col: string;
  value: Scalar; // single value, not array
}

/** Less than or equal to (<=) filter - single value */
interface LessThanOrEqualToColumnFilter {
  col: string;
  value: Scalar; // single value, not array
}

/** Contains substring filter - single string value */
interface ContainsColumnFilter {
  col: string;
  value: string; // single string, not array
}

/** Does not contain substring filter - single string value */
interface NotContainsColumnFilter {
  col: string;
  value: string; // single string, not array
}

/** Starts with prefix filter - single string value */
interface StartsWithColumnFilter {
  col: string;
  value: string; // single string, not array
}

/** Does not start with prefix filter - single string value */
interface NotStartsWithColumnFilter {
  col: string;
  value: string; // single string, not array
}

/** Ends with suffix filter - single string value */
interface EndsWithColumnFilter {
  col: string;
  value: string; // single string, not array
}

/** Does not end with suffix filter - single string value */
interface NotEndsWithColumnFilter {
  col: string;
  value: string; // single string, not array
}

export interface ColumnFiltersApiShape {
  in_filters?: InColumnFilter[];
  not_in_filters?: NotInColumnFilter[];
  between_filters?: BetweenColumnFilter[];
  not_between_filters?: NotBetweenColumnFilter[];
  is_null_filters?: IsNullColumnFilter[];
  is_not_null_filters?: IsNotNullColumnFilter[];
  greater_than_filters?: GreaterThanColumnFilter[];
  greater_than_or_equal_to_filters?: GreaterThanOrEqualToColumnFilter[];
  less_than_filters?: LessThanColumnFilter[];
  less_than_or_equal_to_filters?: LessThanOrEqualToColumnFilter[];
  contains_filters?: ContainsColumnFilter[];
  not_contains_filters?: NotContainsColumnFilter[];
  starts_with_filters?: StartsWithColumnFilter[];
  not_starts_with_filters?: NotStartsWithColumnFilter[];
  ends_with_filters?: EndsWithColumnFilter[];
  not_ends_with_filters?: NotEndsWithColumnFilter[];
}

/**
 * API wire-format column filter — a single discriminated-union entry tagged by
 * ``op``.  Matches the backend ``ColumnFilter`` Pydantic model introduced in
 * gf-insights-api PR #356 (GF-4038).
 *
 * The UI keeps the legacy 16-bucket {@link ColumnFiltersApiShape} as its
 * internal representation; it is flattened to this shape at the API client
 * boundary via {@link toApiColumnFiltersPayload}.
 */
export type ApiColumnFilter =
  | { op: 'in'; col: string; values: Scalar[]; negated?: boolean }
  | { op: 'between'; col: string; values: [Scalar, Scalar]; negated?: boolean }
  | { op: 'is_null'; col: string; negated?: boolean }
  | { op: 'gt'; col: string; value: Scalar; or_equal?: boolean }
  | { op: 'lt'; col: string; value: Scalar; or_equal?: boolean }
  | { op: 'contains'; col: string; value: string; negated?: boolean }
  | { op: 'starts_with'; col: string; value: string; negated?: boolean }
  | { op: 'ends_with'; col: string; value: string; negated?: boolean };

/**
 * API wire-format column filters payload — a single ordered list, replacing
 * the legacy 16 typed buckets.  Matches the backend ``ColumnFilters`` Pydantic
 * model (gf-insights-api PR #356, GF-4038).
 */
export interface ApiColumnFiltersPayload {
  filters: ApiColumnFilter[];
}

/**
 * Flatten the legacy 16-bucket {@link ColumnFiltersApiShape} into the new
 * tagged flat-list payload required by gf-insights-api PR #356 (GF-4038).
 *
 * Bucket order is preserved (in → not_in → between → ... → not_ends_with) so
 * SQL WHERE-clause ordering is stable for caching / hashing.  Empty buckets
 * contribute nothing.
 *
 * Affected endpoints on the backend: data_table, data_metric, chart,
 * growth-segments, csv-export — every route taking ``ColumnFiltersRequestModel``.
 */
export function toApiColumnFiltersPayload(
  shape: ColumnFiltersApiShape | null | undefined
): ApiColumnFiltersPayload {
  if (!shape) return { filters: [] };
  const filters: ApiColumnFilter[] = [];

  for (const f of shape.in_filters ?? []) {
    filters.push({ op: 'in', col: f.col, values: f.values });
  }
  for (const f of shape.not_in_filters ?? []) {
    filters.push({ op: 'in', col: f.col, values: f.values, negated: true });
  }
  for (const f of shape.between_filters ?? []) {
    filters.push({ op: 'between', col: f.col, values: f.values });
  }
  for (const f of shape.not_between_filters ?? []) {
    filters.push({ op: 'between', col: f.col, values: f.values, negated: true });
  }
  for (const f of shape.is_null_filters ?? []) {
    filters.push({ op: 'is_null', col: f.col });
  }
  for (const f of shape.is_not_null_filters ?? []) {
    filters.push({ op: 'is_null', col: f.col, negated: true });
  }
  for (const f of shape.greater_than_filters ?? []) {
    filters.push({ op: 'gt', col: f.col, value: f.value });
  }
  for (const f of shape.greater_than_or_equal_to_filters ?? []) {
    filters.push({ op: 'gt', col: f.col, value: f.value, or_equal: true });
  }
  for (const f of shape.less_than_filters ?? []) {
    filters.push({ op: 'lt', col: f.col, value: f.value });
  }
  for (const f of shape.less_than_or_equal_to_filters ?? []) {
    filters.push({ op: 'lt', col: f.col, value: f.value, or_equal: true });
  }
  for (const f of shape.contains_filters ?? []) {
    filters.push({ op: 'contains', col: f.col, value: f.value });
  }
  for (const f of shape.not_contains_filters ?? []) {
    filters.push({ op: 'contains', col: f.col, value: f.value, negated: true });
  }
  for (const f of shape.starts_with_filters ?? []) {
    filters.push({ op: 'starts_with', col: f.col, value: f.value });
  }
  for (const f of shape.not_starts_with_filters ?? []) {
    filters.push({ op: 'starts_with', col: f.col, value: f.value, negated: true });
  }
  for (const f of shape.ends_with_filters ?? []) {
    filters.push({ op: 'ends_with', col: f.col, value: f.value });
  }
  for (const f of shape.not_ends_with_filters ?? []) {
    filters.push({ op: 'ends_with', col: f.col, value: f.value, negated: true });
  }

  return { filters };
}
